/**
 * Поиск в шапке.
 *
 * Поле, а не только кнопка: искать начинают набором текста, а не поиском
 * кнопки поиска. Результаты падают выпадающим списком прямо под полем —
 * уходить на отдельную страницу ради трёх находок незачем.
 *
 * Ищется то же, что и в командной строке, плюс участники проекта: их имена
 * не хранятся вместе с элементами, поэтому фильтруются на клиенте — их
 * единицы, и отдельный запрос за списком из пяти строк был бы лишним.
 *
 * Ctrl+K по-прежнему открывает командную строку: поле — для поиска, палитра —
 * для поиска и действий. Разные задачи, разные инструменты.
 */

import { SearchIcon, XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import type { ProjectDto, ProjectMemberDto, SearchResultDto } from '@project-os/contracts';
import { useProjectMembers } from '@/api/members';
import { MIN_SEARCH_LENGTH, useProjectSearch } from '@/api/search';
import { useStages } from '@/api/stages';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserAvatar } from '@/components/common/user-avatar';
import { memberColorStyle } from '@/lib/member-color';
import { cn } from '@/lib/utils';
import { getItemIcon } from '@/features/items/item-icon';

/** Задержка перед запросом: набор текста не должен слать запрос на букву. */
const DEBOUNCE_MS = 250;

/** Сравнение с учётом кириллицы: toLowerCase без локали её портит. */
function matches(text: string, query: string): boolean {
  return text.toLocaleLowerCase('ru').includes(query.toLocaleLowerCase('ru'));
}

export function HeaderSearch({ project }: { project: ProjectDto }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Щелчок вне поля закрывает выдачу: она перекрывает содержимое страницы.
  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const searching = debounced.length >= MIN_SEARCH_LENGTH;
  const { data, isPending } = useProjectSearch(project.id, debounced);
  const { data: stages = [] } = useStages(project.id);
  const { data: members = [] } = useProjectMembers(project.id);

  const foundStages = useMemo(
    () => (searching ? stages.filter((stage) => matches(stage.name, debounced)).slice(0, 3) : []),
    [stages, debounced, searching],
  );

  const foundMembers = useMemo(
    () =>
      searching
        ? members
            .filter(
              (member) =>
                matches(member.user.name, debounced) || matches(member.user.email, debounced),
            )
            .slice(0, 3)
        : [],
    [members, debounced, searching],
  );

  const go = (to: string) => {
    setOpen(false);
    setQuery('');
    void navigate(to);
  };

  const total = (data?.totalCount ?? 0) + foundStages.length + foundMembers.length;

  return (
    <div ref={containerRef} className="relative hidden md:block">
      <SearchIcon
        className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <Input
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            setOpen(false);
            event.currentTarget.blur();
          }
        }}
        placeholder="Поиск по проекту"
        aria-label="Поиск по проекту"
        className="h-7 w-56 pr-7 pl-7 text-xs lg:w-72"
      />
      {query ? (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-1/2 right-0.5 size-6 -translate-y-1/2"
          onClick={() => {
            setQuery('');
            setOpen(false);
          }}
          aria-label="Очистить поиск"
        >
          <XIcon className="size-3" />
        </Button>
      ) : null}

      {open && searching ? (
        <div className="absolute top-9 right-0 z-50 w-96 overflow-hidden rounded-xl border border-border-strong bg-popover shadow-overlay">
          {isPending ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">Ищем…</p>
          ) : total === 0 ? (
            <p className="px-3 py-4 text-xs text-muted-foreground">
              Ничего не найдено по запросу «{debounced}».
            </p>
          ) : (
            <ScrollArea className="max-h-[26rem]">
              <ResultGroup
                heading="Элементы"
                results={data?.items ?? []}
                onOpen={(itemId) => go(`/projects/${project.id}/items?item=${itemId}`)}
              />
              <ResultGroup
                heading="Обсуждение"
                results={data?.comments ?? []}
                onOpen={(itemId) => go(`/projects/${project.id}/items?item=${itemId}`)}
              />
              <ResultGroup
                heading="Файлы"
                results={data?.attachments ?? []}
                onOpen={(itemId) => go(`/projects/${project.id}/items?item=${itemId}`)}
              />

              {foundStages.length > 0 ? (
                <Group heading="Этапы">
                  {foundStages.map((stage) => (
                    <button
                      key={stage.id}
                      type="button"
                      className="row-hover flex w-full items-center gap-2 px-3 py-1.5 text-left"
                      onClick={() => go(`/projects/${project.id}/stages`)}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm">{stage.name}</span>
                      <span className="shrink-0 text-2xs text-muted-foreground tabular-nums">
                        {stage.readiness.done} из {stage.readiness.total}
                      </span>
                    </button>
                  ))}
                </Group>
              ) : null}

              {foundMembers.length > 0 ? (
                <Group heading="Участники">
                  {foundMembers.map((member) => (
                    <MemberRow
                      key={member.user.id}
                      member={member}
                      onOpen={() => go(`/projects/${project.id}/items?ownerId=${member.user.id}`)}
                    />
                  ))}
                </Group>
              ) : null}
            </ScrollArea>
          )}
        </div>
      ) : null}
    </div>
  );
}

function Group({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="border-b border-border/40 py-1 last:border-b-0">
      <p className="px-3 py-1 text-2xs font-semibold tracking-wide text-muted-foreground uppercase">
        {heading}
      </p>
      {children}
    </div>
  );
}

function ResultGroup({
  heading,
  results,
  onOpen,
}: {
  heading: string;
  results: SearchResultDto[];
  onOpen: (itemId: string) => void;
}) {
  if (results.length === 0) return null;

  return (
    <Group heading={heading}>
      {results.map((result) => {
        const Icon = result.itemType ? getItemIcon(result.itemType) : SearchIcon;
        return (
          <button
            key={`${result.kind}-${result.id}`}
            type="button"
            className="row-hover flex w-full items-start gap-2 px-3 py-1.5 text-left"
            onClick={() => result.itemId && onOpen(result.itemId)}
          >
            <Icon className="mt-0.5 size-3.5 shrink-0 opacity-70" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">{result.title}</span>
              {result.snippet ? (
                <span className="block truncate text-2xs text-muted-foreground">
                  {result.author ? `${result.author}: ` : ''}
                  {result.snippet}
                </span>
              ) : null}
            </span>
            <span className="shrink-0 text-2xs text-muted-foreground">
              {/* Тип и статус: без них находка не отличима от однофамильца. */}
              {[result.typeLabelRu, result.status].filter(Boolean).join(' · ')}
            </span>
          </button>
        );
      })}
    </Group>
  );
}

function MemberRow({ member, onOpen }: { member: ProjectMemberDto; onOpen: () => void }) {
  return (
    <button
      type="button"
      className="row-hover flex w-full items-center gap-2 px-3 py-1.5 text-left"
      onClick={onOpen}
    >
      <UserAvatar user={member.user} size="sm" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm">{member.user.name}</span>
        <span className="block truncate text-2xs text-muted-foreground">{member.user.email}</span>
      </span>
      <span className="flex shrink-0 items-center gap-1.5">
        <span
          className={cn('member-color size-2 rounded-full')}
          style={memberColorStyle(member.user.id)}
          aria-hidden
        />
        <span className="text-2xs text-muted-foreground">
          {member.openItemCount > 0 ? `${member.openItemCount} в работе` : 'нет открытых'}
        </span>
      </span>
    </button>
  );
}
